import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { Dropdown, Flex, InfoTooltip } from 'ui';
import { AutoComplete } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import debounce from 'lodash.debounce';
import { useDispatch, useSelector } from 'react-redux';
import { clearOptions, searchAddressRequest } from '../containers/geo-filter-actions';
import { urlSigns } from '../../../../utils/app-configs';
import {
  HighlightSpan,
  ItemTitle,
  SingleLineSpan,
  SupportingIcon,
} from '../../entity-filter/styles';
import { sanitize } from '../../../../utils/helpers';

const Title = styled.div`
  font-weight: 400;
  font-size: 16px;
`;

const AddressInput = styled(AutoComplete)`
  width: calc(${p => p.isColumnFilter ? '100%' : '90%'} - 140px) !important;
  height: 100% !important;
  border-radius: 6px !important;
  font-weight: 400 !important;
  .ant-select-selector {
    font-size: 16px !important;
  }
`;

const TimingRange = styled(Dropdown)`
  margin: 0 6px;
  width: 120px;
`;

const AddressOptions = styled(Dropdown)`
  width: 140px;
  height: 100% !important;
  margin-right: 3px;
`;

const FirstLine = styled.div`
  margin-bottom: 5px;
`;

const SecondLine = styled.div`
  height: 32px;
  display: flex;
  align-items: flex-start;
  position: relative;
`;

const LoadingIcon = styled(LoadingOutlined)`
  position: absolute;
  left: 87%;
  top: 10px;
  z-index: 1;
`;

const addressOptionTypes = {
  any: 'any',
  installedat: 'ia',
  fse: 'fse',
};

const GeoFilter = ({
  description,
  onChange,
  activeFilters = [],
  timeRanges = [],
  addressOptions = [],
  options = [],
  intl,
  isColumnFilter,
  disabled,
  info = '',
}) => {
  const dispatch = useDispatch();
  const [address, setAddress] = useState();
  const [timeRange, setTimeRange] = useState(timeRanges?.find(t => t.isDefault).id);
  const [addressOption, setAddressOption] = useState(addressOptions?.find(t => t.isDefault).id);
  const { isLoadingAddresses, addresses = [] } = useSelector(state => state.geofilter);

  useEffect(() => {
    if (activeFilters?.length) {
      const [addressType, time, addressValue] = activeFilters[0].split(urlSigns.valueSeparator);
      const option = options?.find(o => o.id === addressValue);
      setTimeRange(Number(time));
      setAddressOption(addressType);
      setAddress({ value: addressValue, label: option?.description || addressValue });
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line max-len
    const currentFilter = `${addressOption}${urlSigns.valueSeparator}${timeRange}${urlSigns.valueSeparator}${address?.value}`;
    if (activeFilters?.length && activeFilters[0] !== currentFilter) {
      if (!currentFilter) {
        const [addressType, time, addressValue] = activeFilters[0].split(urlSigns.valueSeparator);
        const option = options?.find(o => o.id === addressValue);
        setTimeRange(Number(time));
        setAddressOption(addressType);
        setAddress({ value: addressValue, label: option?.description || addressValue });
      }
    }
  }, [activeFilters]);

  const searchAddress = useCallback(
    debounce((v) => {
      const endpoint = addressOptions?.find(ao => ao.id === addressOption)?.endpoint;
      dispatch(searchAddressRequest({
        value: v,
        endpoint,
      }));
    }, 300),
    [addressOption],
  );

  const updateFilterValue = ({ tr, ao, ad }) => {
    // eslint-disable-next-line max-len
    const filter = `${ao || addressOption}${urlSigns.valueSeparator}${tr || timeRange}${urlSigns.valueSeparator}${ad || address?.value}`;
    onChange([filter]);
  };

  const handleSelect = (value) => {
    const option = addresses?.find(a => a.id === value);
    setAddress({ value, label: sanitize(option?.line1 || value) });
    updateFilterValue({ ad: value });
  };

  const handleInputChange = (v) => {
    setAddress(s => ({ ...s, label: v }));
    if (v.length >= 3) {
      searchAddress(v);
    }
  };

  const handleClear = () => {
    // eslint-disable-next-line no-undefined
    setAddress(undefined);
    dispatch(clearOptions());
    if (address?.value) {
      onChange([]);
    }
  };

  const handleTimeRangeChange = (v) => {
    setTimeRange(v);
    if (address?.value) {
      updateFilterValue({ tr: v });
    }
  };

  const handleTypeChange = (v) => {
    setAddressOption(v);
    handleClear();
  };

  const getInputPlaceholder = () => {
    if (addressOption === addressOptionTypes.any) {
      return intl.formatMessage({ id: 'worklist.filters.geoFilter.ANY_PLACEHOLDER' });
    } else if (addressOption === addressOptionTypes.installedat) {
      return intl.formatMessage({ id: 'worklist.filters.geoFilter.IA_PLACEHOLDER' });
    }
    return intl.formatMessage({ id: 'worklist.filters.geoFilter.FSE_PLACEHOLDER' });
  };

  const renderTitleLine = (text, icon) => <SingleLineSpan>
    {icon ? <SupportingIcon type={icon} marginRight /> : null}
    <ItemTitle dangerouslySetInnerHTML={{ __html: text }} />
  </SingleLineSpan>;

  const renderLine = (text, icon) => <SingleLineSpan>
    {icon ? <SupportingIcon type={icon} marginRight /> : null}
    <HighlightSpan dangerouslySetInnerHTML={{ __html: text }} />
  </SingleLineSpan>;

  const renderOption = option => addressOption === addressOptionTypes.any ?
    option.line1 :
    <Flex ffcn>
      {renderTitleLine(option?.line1, option?.line1Icon)}
      {option?.line2 && renderLine(option?.line2, option?.line2Icon)}
      {option?.line3 && renderLine(option?.line3, option?.line3Icon)}
      {option?.line4 && renderLine(option?.line4, option?.line4Icon)}
    </Flex>;

  return (
    <>
      {!isColumnFilter && <Title>
        {description.toUpperCase()}
        <InfoTooltip content={info} />
      </Title>
      }
      <FirstLine>
        <span>{intl.formatMessage({ id: 'worklist.filters.geoFilter.MAXIMUM' })}</span>
        <TimingRange
          onChange={handleTimeRangeChange}
          values={timeRanges?.sort((a, b) => a.sort - b.sort)}
          value={timeRange}
          disabled={disabled}
          valueLabel='description'
          valueKey='id'
        />
        <span>
          {`${intl.formatMessage({ id: 'worklist.filters.geoFilter.AWAY_FROM' })?.toLowerCase()}:`}
        </span>
      </FirstLine>
      <SecondLine>
        <AddressOptions
          onChange={handleTypeChange}
          values={addressOptions?.sort((a, b) => a.sort - b.sort)}
          value={addressOption}
          disabled={disabled}
          valueLabel='description'
          valueKey='id'
        />
        <AddressInput
          allowClear={true}
          value={address?.label}
          disabled={disabled}
          onSearch={handleInputChange}
          onClear={handleClear}
          placeholder={getInputPlaceholder()}
          isColumnFilter={isColumnFilter}
          onSelect={handleSelect}
          options={addresses?.map(a => ({
            label: renderOption(a),
            value: a.id,
          }))}
        />
        {isLoadingAddresses && <LoadingIcon />}
      </SecondLine>
    </>
  );
};

export default injectIntl(GeoFilter);

GeoFilter.propTypes = {
  description: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  activeFilters: PropTypes.array,
  timeRanges: PropTypes.array,
  addressOptions: PropTypes.array,
  options: PropTypes.array,
  intl: PropTypes.object,
  isColumnFilter: PropTypes.bool,
  disabled: PropTypes.bool,
  info: PropTypes.string,
};
